import React, { useMemo, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useInput } from "react-admin";
import config from "../config/config";

const resolveFileFromFieldValue = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length > 0) return resolveFileFromFieldValue(value[0]);
  if (value.rawFile) return value.rawFile;
  if (typeof File !== "undefined" && value instanceof File) return value;
  return null;
};

const ThumbnailUploadInput = ({ source, nameValue, thumbnailUrlValue }) => {
  const inputRef = useRef(null);
  const {
    field: { value, onChange },
  } = useInput({ source });

  const thumbnailCfg = config?.media_thumbnail || {};
  const width = Number(thumbnailCfg.width || 320);
  const height = Number(thumbnailCfg.height || 180);
  const maxUploadBytes = Number(thumbnailCfg.max_upload_bytes || 1048576);
  const currentFile = resolveFileFromFieldValue(value);

  const previewUrl = useMemo(() => {
    if (!currentFile) return thumbnailUrlValue || "";
    return URL.createObjectURL(currentFile);
  }, [currentFile, thumbnailUrlValue]);

  const initials = useMemo(() => {
    const normalized = String(nameValue || "").trim();
    if (!normalized) return "";
    return normalized
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0] || "")
      .join("")
      .toUpperCase();
  }, [nameValue]);

  const handleFileSelected = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (file.size > maxUploadBytes) return;
    onChange([{ rawFile: file, title: file.name }]);
  };

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Box
        sx={{
          width: `${width}px`,
          height: `${height}px`,
          position: "relative",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.25)",
          background: "#000",
        }}
      >
        {previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt="Thumbnail"
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              color: "#fff",
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>SEM IMAGEM</Typography>
            {!!initials && <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{initials}</Typography>}
          </Box>
        )}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.45)",
            opacity: 0,
            transition: "opacity 0.15s ease-in-out",
            "&:hover": { opacity: 1 },
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => inputRef.current && inputRef.current.click()}
            sx={{ textTransform: "none" }}
          >
            Alterar thumbnail
          </Button>
        </Box>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />
    </Box>
  );
};

export default ThumbnailUploadInput;
